import React from 'react';
import styled from 'styled-components';
import { BookOpen, NotebookText } from 'lucide-react';
import { Note, isSource } from '@annote/types';
import { colors, ui } from '@annote/core/styles';
import { usePanelContext } from 'src/context/PanelContext';

const PinnedButton = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1; /* Below the card menu (which is z-index: 2) */
  background-color: ${colors.button.selected};
  border-radius: 4px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* Non-interactive - the card menu button handles clicks */
`;

// Shows a pinned indicator when a note is active in its panel AND the panel is open
// This is covered by the CardMenu on hover, which handles the actual click interaction
export const PinnedIndicator: React.FC<{ note: Note }> = ({ note }) => {
  // Only sources and takeaways can be pinned in panels
  if (note.type !== 'source' && note.type !== 'takeaway') return null;

  const { panels } = usePanelContext();

  const isSrc = isSource(note);
  const icon = isSrc ? BookOpen : NotebookText;
  const panelId = isSrc ? 'reader' : 'writer';

  // Only show if note is active AND panel is open
  const shouldShow = panels[panelId].activeId === note.id && panels[panelId].isOpen;

  if (!shouldShow) return null;

  const IconComponent = icon;
  const iconSize = ui.getIconSizePx('cardButton');

  return (
    <PinnedButton>
      <IconComponent width={iconSize} height={iconSize} stroke={colors.icon.primary} strokeWidth={ui.iconStrokeWidth} />
    </PinnedButton>
  );
};
